import './horizontal-scroll-native.scss';
import Base from '../base/base';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Configuration options for HorizontalScrollNative
 */
interface HorizontalScrollNativeOptions {
  /** Selector for the section that gets the native-scroll modifier class */
  sectionSelector?: string;
  /** Selector for the element that becomes the horizontal scroll container */
  scrollableSelector?: string;
  /** Modifier class added to the section to activate native-scroll CSS */
  modifierClass?: string;
  /** Enable the "nudge" intro animation hinting that the row is scrollable */
  animateIntro?: boolean;
  /** Viewport percentage from top to trigger the intro nudge (0-100), default 60 */
  introTriggerPoint?: number;
  /** Distance in pixels to nudge the scroll position on intro, default 60 */
  nudgeDistance?: number;
  /** Duration of the "out" nudge in seconds, default 0.35 */
  nudgeDuration?: number;
  /** Duration of the "snap back" in seconds, default 0.25 */
  nudgeSnapBackDuration?: number;
  /** Enable debug logging */
  debug?: boolean;
  [key: string]: any;
}

/**
 * HorizontalScrollNative
 *
 * Lightweight alternative to HorizontalScrollSection. Instead of converting
 * vertical scroll into horizontal translation with GSAP/ScrollTrigger, it
 * un-pins the section (via a modifier class + component SCSS) and lets the
 * browser handle horizontal scrolling natively, with a styled scrollbar.
 *
 * It only uses GSAP for a one-shot "nudge" intro animation that scrolls the
 * row slightly left and snaps back to hint that the section is scrollable.
 *
 * @requires gsap
 * @requires ScrollTrigger (GSAP plugin)
 */
export default class HorizontalScrollNative extends Base {
  private config: Required<Omit<HorizontalScrollNativeOptions, 'debug'>>;
  private section: HTMLElement | null;
  private scrollable: HTMLElement | null;

  // State
  private hasAnimatedIn: boolean = false;
  private proxy: { x: number } = { x: 0 };

  // ScrollTrigger instances (for cleanup)
  private visibilityTrigger: ScrollTrigger | null = null;
  private introTrigger: ScrollTrigger | null = null;

  /**
   * @param options - Configuration options
   */
  constructor(options: HorizontalScrollNativeOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);

    // Merge user config with defaults
    this.config = {
      sectionSelector: '.process-steps-section',
      scrollableSelector: '.process-steps-scrollable',
      modifierClass: 'process-steps-section--native',
      animateIntro: true,
      introTriggerPoint: 33,
      nudgeDistance: 100,
      nudgeDuration: 0.5,
      nudgeSnapBackDuration: 0.35,
      ...options
    };

    // Get DOM references
    this.section = document.querySelector(this.config.sectionSelector);
    this.scrollable = document.querySelector(this.config.scrollableSelector);

    // Validate required elements exist
    if (!this.section || !this.scrollable) {
      console.error('HorizontalScrollNative: Required DOM elements not found', {
        section: this.config.sectionSelector,
        scrollable: this.config.scrollableSelector
      });
      return;
    }

    this.init();
  }

  /**
   * Initialize native horizontal scroll
   */
  init(): void {
    if (!this.section) return;

    // Activate native-scroll layout/scrollbar styles
    this.section.classList.add(this.config.modifierClass);
    this.log('HorizontalScrollNative initialized');

    // Set up the intro nudge unless disabled or the user prefers reduced motion
    if (this.config.animateIntro && !this.prefersReducedMotion()) {
      this.setupIntroAnimation();
    }

    // Adding the modifier class collapses the 400vh pinned layout, which shifts
    // every ScrollTrigger position on the page. Recalculate so the triggers
    // (including the intro nudge) measure against the final, un-pinned layout;
    // this also absorbs late shifts from the lazy-loaded card images.
    ScrollTrigger.refresh();
  }

  /**
   * Whether the user has requested reduced motion
   */
  private prefersReducedMotion(): boolean {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * Set up ScrollTrigger instances for the intro nudge.
   * One trigger resets the "already nudged" guard when the section leaves the
   * viewport; another fires the nudge when the section scrolls into view.
   */
  private setupIntroAnimation(): void {
    const scrollable = this.scrollable;
    if (!scrollable) return;

    // Reset the guard whenever the tiles fully leave the viewport so the
    // nudge can replay on the next entrance.
    this.visibilityTrigger = ScrollTrigger.create({
      trigger: scrollable,
      start: 'top bottom',
      end: 'bottom top',
      onLeave: () => {
        this.hasAnimatedIn = false;
      },
      onLeaveBack: () => {
        this.hasAnimatedIn = false;
      }
    });

    // Fire the nudge when the tiles themselves cross the trigger point (i.e.
    // become visible) rather than when the section's top does — the tiles sit
    // below the section header, so a section-based trigger fires too early.
    this.introTrigger = ScrollTrigger.create({
      trigger: scrollable,
      start: `top ${this.config.introTriggerPoint}%`,
      onEnter: () => this.nudge(),
      onEnterBack: () => this.nudge()
    });
  }

  /**
   * Animate the scroll position out a little and snap back, hinting that the
   * row is horizontally scrollable. Tweens the real scrollLeft via a proxy so
   * it stays consistent with native scrolling and the scrollbar.
   */
  private nudge(): void {
    if (this.hasAnimatedIn || !this.scrollable) return;

    // Nothing to hint at if the row isn't actually overflowing.
    const maxScroll = this.scrollable.scrollWidth - this.scrollable.clientWidth;
    if (maxScroll <= 0) return;

    this.hasAnimatedIn = true;
    this.log('Nudging to hint scrollability');

    const target = Math.min(this.config.nudgeDistance, maxScroll);

    // Kill any in-flight nudge and start from the current position.
    gsap.killTweensOf(this.proxy);
    this.proxy.x = this.scrollable.scrollLeft;

    gsap.to(this.proxy, {
      x: target,
      duration: this.config.nudgeDuration,
      ease: 'power2.out',
      onUpdate: () => this.applyScroll(),
      onComplete: () => {
        gsap.to(this.proxy, {
          x: 0,
          duration: this.config.nudgeSnapBackDuration,
          ease: 'power3.inOut',
          onUpdate: () => this.applyScroll()
        });
      }
    });
  }

  /**
   * Write the proxy value to the container's scroll position
   */
  private applyScroll(): void {
    if (this.scrollable) {
      this.scrollable.scrollLeft = this.proxy.x;
    }
  }

  /**
   * Cleanup: kill triggers/tweens and remove the modifier class
   */
  destroy(): void {
    if (this.visibilityTrigger) {
      this.visibilityTrigger.kill();
      this.visibilityTrigger = null;
    }
    if (this.introTrigger) {
      this.introTrigger.kill();
      this.introTrigger = null;
    }

    gsap.killTweensOf(this.proxy);

    if (this.section) {
      this.section.classList.remove(this.config.modifierClass);
    }

    this.log('HorizontalScrollNative destroyed');
  }
}
